
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { canUseFeature } from "@/services/companies";
import { areArraysEquals, removeVuetifyFileInputBug } from "@/utils/misc";
import { formRules } from "@/utils/formRules";
import RelayAddGuests from "@/components/relays/RelayAddGuests.vue";
import RelayAddManagers from "@/components/relays/RelayAddManagers.vue";
import RelayAddReferents from "@/components/relays/RelayAddReferents.vue";
import SchedulePicker from "@/components/SchedulePicker.vue";
import { Getter } from "vuex-class";

@Component({
    components: { SchedulePicker, RelayAddReferents, RelayAddManagers, RelayAddGuests },
})
export default class RelayInfo extends Vue {

    get lang(): string {
        return this.$root.$i18n.locale;
    }

    get manageStockActive() {
        return canUseFeature(this.envCompany, "manageStock");
    }

    get formattedCategoriesValues(): any[] {
        const lang: string = this.lang;
        return this.categories.map((category: any) => {
            return { text: category[lang], value: category.id };
        });
    }


    @Prop({ default: false })
    public displayDialog: boolean;

    @Prop({ default: null })
    public relay: any;

    @Getter private envCompany: any;
    private removeVuetifyFileInputBug: (document: any) => void = removeVuetifyFileInputBug;

    private currentRelay: any = null;
    private uploadedImageSrc: any = null;
    private changeAvatar: boolean = false;
    private avatar: any = null;
    private isSearchingCategories: boolean = false;
    private searchCategory: string = "";
    private categories: any[] = [];
    private valid: boolean = true;
    private rules: object = formRules;
    private isNewFileUploaded: boolean = false;
    private isFileDeleted: boolean = false;
    private loadingSaveRelay: boolean = false;
    private initManagerGuest = {
        manager: null,
        guest: null,

    };

    @Watch("displayDialog")
    public handlerShowDialog(newVal, oldVal) {
        if (newVal && !oldVal) {
            this.reset();
        }
    }

    private mounted(): void {
        this.getCategories();
        this.reset();

    }

    private beforeUpdate(): void {
        this.removeVuetifyFileInputBug(document);
    }

    private getCategories(): void {
        this.isSearchingCategories = true;
        this.$store.dispatch("categories/getAll")
            .then((res) => {
                this.categories = res.categories;
                this.isSearchingCategories = false;
            });
    }

    private reset() {
        this.currentRelay = { ...this.relay, addDefaultUsersToKeys: {}, defaultUsers: {} };
        this.$store.dispatch("relays/getPresetUsersById", {
            id: this.currentRelay.id,
        }).then((res) => {
            const manager = res
                .filter((m) => m.role === "MANAGER")
                .map((t) => ({
                    ...t?.Group,
                    preset: true,
                }));
            Vue.set(this.currentRelay, "manager", manager);
            this.initManagerGuest.manager = JSON.parse(JSON.stringify(manager));

            const guest = res
                .filter((m) => m.role === "GUEST")
                .map((t) => ({
                    ...t?.Group,
                    preset: true,
                }));
            Vue.set(this.currentRelay, "guest", guest);
            this.initManagerGuest.guest = JSON.parse(JSON.stringify(guest));

        });

    }

    private fileChange(): void {
        if (this.avatar) {
            const fr = new FileReader();
            fr.readAsDataURL(this.avatar);
            fr.addEventListener("load", () => {
                this.uploadedImageSrc = fr.result;
            });
            this.isNewFileUploaded = true;
            this.isFileDeleted = false;
        } else {
            this.uploadedImageSrc = null;
        }
    }

    private deleteAvatar(): void {
        this.avatar = null;
        this.uploadedImageSrc = null;
        this.currentRelay.picturePath = null;
        this.isFileDeleted = true;
        this.isNewFileUploaded = false;
    }

    private closeEdit(): void {
        this.avatar = null;
        this.currentRelay.addDefaultUsersToKeys = {};
        this.$emit("reset");
    }

    private defaultUsersChanged(userType: string): boolean {
        const oldUsers: any[] = this.initManagerGuest[userType];
        const newUsers: any[] = this.currentRelay[userType];
        const comparisonFunction: (x, y) => boolean = (x, y) => x.id === y.id && x.name === y.name;
        return !areArraysEquals(oldUsers, newUsers, comparisonFunction);
    }

    private async save(): Promise<void> {
        this.loadingSaveRelay = true;
        ["guest", "manager"].forEach((userType) => {
            this.currentRelay.defaultUsers[userType] = this.currentRelay[userType]
                ?.filter((group) => group.Users?.length !== 0)
                ?.map((group) => ({ id: group.id }));
        });
        if (typeof this.currentRelay.defaultCategoryId === "undefined") {
            this.currentRelay.defaultCategoryId = null;
        }
        if (this.isNewFileUploaded) {
            const formData = new FormData();
            formData.append("file", this.avatar);
            formData.append("id", this.relay.id);
            formData.append("fileType", "relay-picture");
            formData.append("fileName", this.avatar.name);
            this.$store.dispatch("files/upload", { formData });
            this.currentRelay.picturePath = `/api/files/relays/${this.currentRelay.id}/pictures/relay-picture`;
        }
        if (this.isFileDeleted) {
            this.$store.dispatch("files/delete", { id: this.relay.id, fileType: "relay-picture" });
        }
        this.currentRelay.referents = this.currentRelay.Referents.map((ref) => ref.id);
        delete this.currentRelay.guest;
        delete this.currentRelay.manager;
        delete this.currentRelay.Referents;
        await this.$store.dispatch("relays/update", {
            id: this.relay.id,
            relay: this.currentRelay,
        }).then(async () => {
            this.$store.commit("alerts/displaySuccess", {
                msg: this.$i18n?.t("actions.saved"),
            });
            this.closeEdit();
        }).catch((error) => {
            this.$store.commit("alerts/displayHttpError", { error });
        }).finally(() => {
            this.loadingSaveRelay = false;
            this.closeEdit();
        });
    }

}
